import { useEffect, useRef, useState } from "react";
import jsPDF from "jspdf";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Search, Printer, AlertTriangle, Trash2, ShieldCheck, Download, Pencil, Plus, Tag, FileUp, Loader2 } from "lucide-react";
import InvoiceIntake from "../components/InvoiceIntake";
import LotDetailDialog from "../components/intake/LotDetailDialog";
import { Image as ImageIcon, Layers } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { QRCodeCanvas } from "qrcode.react";
import { ProductSheets } from "../components/ProductSheets";

const today = () => new Date();
const daysLeft = (e) => { try { return Math.ceil((new Date(e) - today()) / 86400000); } catch { return null; } };
const WALLETS = [{ v: "contante", l: "Contanti" }, { v: "sumup", l: "SumUp" }, { v: "banca", l: "Banca" }, { v: "deposito", l: "Deposito" }, { v: "fornitore", l: "Sospeso (debito fornitore)" }];
const emptyManual = { name: "", category: "", unit: "pz", quantity: "", min_quantity: "0", supplier: "", expiry_date: "", lotto: "", cost: "", ledger: "ufficiale", payment_method: "contante", tax_rate: "22" };

export default function Batches() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";

  // ---- Merci ricevute (lotti) ----
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [lotId, setLotId] = useState(null);
  const [prodLots, setProdLots] = useState(null);
  const [category, setCategory] = useState("");
  const [month, setMonth] = useState("");
  const [onlyExp, setOnlyExp] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [pFrom, setPFrom] = useState("");
  const [pTo, setPTo] = useState("");
  const [inspOpen, setInspOpen] = useState(false);
  const [inspToken, setInspToken] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const emptyBatch = { product: "", category: "", lotto: "", expiry: "", quantity: 0, unit: "pz", supplier: "", received_date: "", notes: "" };
  const [editForm, setEditForm] = useState(emptyBatch);
  const [editId, setEditId] = useState(null);

  // ---- Scorte (magazzino) ----
  const [stock, setStock] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState(emptyManual);
  const [stockEditId, setStockEditId] = useState(null);

  // ---- Import CSV magazzino ----
  const csvRef = useRef(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);

  const onCsvPick = async (file) => {
    if (!file) return;
    setCsvFile(file); setCsvPreview(null); setCsvBusy(true); setCsvOpen(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/inventory/import-csv/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCsvPreview(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setCsvOpen(false); }
    finally { setCsvBusy(false); }
  };
  const doCsvImport = async () => {
    if (!csvFile) return;
    setCsvBusy(true);
    try {
      const fd = new FormData(); fd.append("file", csvFile);
      const { data } = await api.post("/inventory/import-csv/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import completato: ${data.batches} lotti · ${data.inventory_created} nuovi prodotti · ${data.inventory_updated} aggiornati`);
      setCsvOpen(false); setCsvFile(null); setCsvPreview(null); loadStock(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCsvBusy(false); }
  };

  const loadStock = async () => { try { setStock((await api.get("/inventory")).data); } catch { /* noop */ } };
  const loadSuppliers = async () => { try { setSuppliers((await api.get("/suppliers")).data); } catch { /* noop */ } };
  const openProdLots = async (i) => {
    setProdLots({ name: i.name, lots: null });
    try { const { data } = await api.get("/intake/product-lots", { params: { name: i.name } }); setProdLots({ name: i.name, lots: data }); }
    catch { setProdLots({ name: i.name, lots: [] }); }
  };


  const openManualNew = () => { setStockEditId(null); setManual(emptyManual); setManualOpen(true); };
  const openManualEdit = (i) => {
    setStockEditId(i.id);
    setManual({ name: i.name || "", category: i.category || "", unit: i.unit || "pz", quantity: String(i.quantity ?? ""), min_quantity: String(i.min_quantity ?? "0"), supplier: i.supplier || "", expiry_date: i.expiry_date || "", lotto: i.lotto || "", cost: "", ledger: "ufficiale", payment_method: "contante", tax_rate: "22" });
    setManualOpen(true);
  };
  const saveManual = async () => {
    if (!manual.name.trim()) return toast.error("Nome obbligatorio");
    if (stockEditId) {
      try {
        await api.put(`/inventory/${stockEditId}`, { name: manual.name, category: manual.category, unit: manual.unit, quantity: parseFloat(manual.quantity) || 0, min_quantity: parseFloat(manual.min_quantity) || 0, supplier: manual.supplier, expiry_date: manual.expiry_date });
        toast.success("Prodotto aggiornato"); setManualOpen(false); setStockEditId(null); loadStock();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      return;
    }
    if (!manual.lotto.trim()) return toast.error("Il lotto è obbligatorio");
    try {
      const payload = {
        name: manual.name, category: manual.category, unit: manual.unit,
        quantity: parseFloat(manual.quantity) || 0, min_quantity: parseFloat(manual.min_quantity) || 0,
        supplier: manual.supplier, expiry_date: manual.expiry_date, lotto: manual.lotto,
        cost: parseFloat(manual.cost) || 0, ledger: manual.ledger, payment_method: manual.payment_method,
        tax_rate: parseFloat(manual.tax_rate) || 0,
      };
      const { data } = await api.post("/inventory/manual", payload);
      toast.success(data.transaction_created ? "Prodotto caricato e spesa registrata" : "Prodotto caricato");
      setManualOpen(false); setManual(emptyManual); loadStock(); load(); loadSuppliers();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const adjust = async (i, delta) => {
    const qty = Math.max(0, (i.quantity || 0) + delta);
    await api.put(`/inventory/${i.id}`, { name: i.name, category: i.category || "", unit: i.unit || "pz", quantity: qty, min_quantity: i.min_quantity || 0, supplier: i.supplier || "", expiry_date: i.expiry_date || "" });
    loadStock();
  };
  const removeStock = async (id) => { if (!window.confirm("Eliminare questo prodotto dal magazzino?")) return; await api.delete(`/inventory/${id}`); toast.success("Eliminato"); loadStock(); };

  const printLabel = (b) => {
    const W = 50, H = 30, M = 3, maxW = W - M * 2;
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: [W, H] });
    let y = M + 2.5;
    doc.setFont("helvetica", "bold"); doc.setFontSize(8);
    const name = doc.splitTextToSize(String(b.product || "Prodotto"), maxW).slice(0, 2);
    doc.text(name, M, y); y += 3.4 * name.length + 0.5;
    doc.setDrawColor(190); doc.line(M, y, W - M, y); y += 2.6;
    doc.setFontSize(6);
    const row = (l, v) => {
      doc.setFont("helvetica", "bold"); doc.text(l, M, y);
      doc.setFont("helvetica", "normal");
      const vv = doc.splitTextToSize(String(v || "—"), maxW - 13).slice(0, 2);
      doc.text(vv, M + 13, y);
      y += 3.1 * vv.length;
    };
    row("Lotto:", b.lotto);
    row("Ingresso:", b.received_date);
    row("Scadenza:", b.expiry);
    row("Fornitore:", b.supplier);
    doc.autoPrint();
    const url = doc.output("bloburl");
    const w = window.open(url, "_blank");
    if (!w) doc.save(`etichetta-${(b.product || "lotto").replace(/[^a-z0-9]/gi, "_")}.pdf`);
  };

  const showIva = manual.ledger === "ufficiale";
  const costPrev = parseFloat(manual.cost) || 0;
  const ratePrev = parseFloat(manual.tax_rate) || 0;
  const imponibilePrev = showIva && ratePrev > 0 ? costPrev / (1 + ratePrev / 100) : costPrev;

  const openEditBatch = (b) => {
    setEditId(b.id);
    setEditForm({ product: b.product || "", category: b.category || "", lotto: b.lotto || "", expiry: b.expiry || "", quantity: b.quantity || 0, unit: b.unit || "pz", supplier: b.supplier || "", received_date: b.received_date || "", notes: b.notes || "" });
    setEditOpen(true);
  };
  const saveBatch = async () => {
    if (!editForm.product.trim()) return toast.error("Inserisci il prodotto");
    try {
      await api.put(`/batches/${editId}`, { ...editForm, quantity: parseFloat(editForm.quantity) || 0 });
      toast.success("Lotto aggiornato"); setEditOpen(false); setEditId(null); load();
    } catch { toast.error("Impossibile salvare"); }
  };

  const openInspection = async () => {
    try { const { data } = await api.get("/settings/inspection-token"); setInspToken(data.token); setInspOpen(true); }
    catch { toast.error("Impossibile generare il QR ispezione"); }
  };
  const inspUrl = inspToken ? `${window.location.origin}/ispezione/${inspToken}` : "";
  const downloadInspQr = () => {
    const c = document.querySelector('[data-testid="insp-qr"] canvas');
    if (!c) return;
    const a = document.createElement("a"); a.download = "qr-ispezione.png"; a.href = c.toDataURL("image/png"); a.click();
  };

  const load = async () => {
    const p = new URLSearchParams();
    if (q) p.append("q", q);
    if (category) p.append("category", category);
    if (month) p.append("month", month);
    if (onlyExp) p.append("expiring_days", "14");
    const { data } = await api.get(`/intake/lots?${p.toString()}`);
    setItems(data);
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, category, month, onlyExp]);
  useEffect(() => { loadStock(); loadSuppliers(); /* eslint-disable-next-line */ }, []);

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa merce ricevuta dalla tracciabilità?")) return;
    try { await api.delete(`/batches/${id}`); toast.success("Merce eliminata"); load(); }
    catch { toast.error("Impossibile eliminare"); }
  };

  const doPrint = (list, label) => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    let y = 18; const left = 14;
    doc.setFontSize(16); doc.setTextColor(194, 65, 39); doc.text("Dogana 2.0 — Merci ricevute", left, y);
    doc.setFontSize(10); doc.setTextColor(90); y += 7;
    doc.text(label, left, y); y += 8;
    doc.setTextColor(26); doc.setFontSize(9); doc.setFont("helvetica", "bold");
    const xs = [14, 68, 98, 122, 158, 196];
    ["Prodotto", "Lotto", "Ricevuto", "Fornitore", "Scadenza", "Qtà"].forEach((h, i) => doc.text(h, xs[i], y, i === 5 ? { align: "right" } : undefined));
    doc.setFont("helvetica", "normal"); y += 2; doc.line(left, y, 196, y); y += 5;
    list.forEach((it) => {
      if (y > 285) { doc.addPage(); y = 18; }
      doc.text(String(it.product || "").slice(0, 30), xs[0], y);
      doc.text(String(it.lotto || "-").slice(0, 15), xs[1], y);
      doc.text(it.received_date || "-", xs[2], y);
      doc.text(String(it.supplier || "-").slice(0, 18), xs[3], y);
      doc.text(it.expiry || "-", xs[4], y);
      doc.text(`${it.quantity || 0} ${it.unit || ""}`, xs[5], y, { align: "right" });
      y += 6;
    });
    if (!list.length) doc.text("Nessuna merce nel periodo selezionato.", left, y);
    doc.save("merci-ricevute.pdf");
  };

  const printPeriod = async () => {
    const p = new URLSearchParams();
    if (q) p.append("q", q);
    if (category) p.append("category", category);
    if (pFrom) p.append("from_date", pFrom);
    if (pTo) p.append("to_date", pTo);
    try {
      const { data } = await api.get(`/batches?${p.toString()}`);
      const label = (pFrom || pTo) ? `Periodo: ${pFrom || "inizio"} → ${pTo || "oggi"}` : "Tutte le ricezioni";
      doPrint(data, label);
      setPrintOpen(false);
    } catch { toast.error("Errore nella stampa"); }
  };

  return (
    <div>
      <PageHeader title="Magazzino" subtitle="Scorte, merci ricevute, lotti, scadenze e schede prodotto" testid="warehouse-header">
        <div className="flex gap-2"><InvoiceIntake onDone={() => { load(); loadStock(); }} docType="fattura" /><InvoiceIntake onDone={() => { load(); loadStock(); }} docType="scontrino" /></div>
      </PageHeader>

      <Tabs defaultValue="scorte">
        <TabsList className="mb-4" data-testid="warehouse-tabs">
          <TabsTrigger value="scorte" data-testid="tab-scorte">Scorte</TabsTrigger>
          <TabsTrigger value="merci" data-testid="tab-merci">Merci ricevute</TabsTrigger>
          <TabsTrigger value="schede" data-testid="tab-schede">Schede prodotto</TabsTrigger>
        </TabsList>

        {/* SCORTE */}
        <TabsContent value="scorte">
          {canManage && (
            <div className="flex justify-end gap-2 mb-4">
              <input ref={csvRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { onCsvPick(e.target.files?.[0]); e.target.value = ""; }} data-testid="csv-import-input" />
              <Button onClick={() => csvRef.current?.click()} variant="outline" className="rounded-full" data-testid="import-csv-button"><FileUp className="w-4 h-4 mr-1" /> Importa CSV</Button>
              <Button onClick={openManualNew} className="rounded-full" data-testid="add-inventory-button"><Plus className="w-4 h-4 mr-1" /> Nuovo prodotto</Button>
            </div>
          )}
          <Card className="rounded-xl overflow-hidden">
            <Table data-testid="inventory-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Prodotto</TableHead><TableHead>Categoria</TableHead>
                  <TableHead className="text-right">Quantità</TableHead><TableHead className="text-right">Min.</TableHead>
                  <TableHead>Fornitore</TableHead><TableHead>Scadenza</TableHead>{canManage && <TableHead></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {stock.length === 0 && <TableRow><TableCell colSpan={canManage ? 7 : 6} className="text-center text-muted-foreground py-8">Nessun prodotto in magazzino</TableCell></TableRow>}
                {stock.map((i) => {
                  const low = (i.quantity || 0) <= (i.min_quantity || 0);
                  return (
                    <TableRow key={i.id} data-testid={`inventory-row-${i.id}`}>
                      <TableCell className="font-medium"><span className="flex items-center gap-2">{low && <AlertTriangle className="w-4 h-4 text-destructive" />}{i.name}<button type="button" className="text-primary opacity-70 hover:opacity-100" title="Lotti in magazzino" onClick={() => openProdLots(i)} data-testid={`product-lots-${i.id}`}><Layers className="w-3.5 h-3.5" /></button></span></TableCell>
                      <TableCell>{i.category || "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-2">
                          {canManage && <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => adjust(i, -1)} data-testid={`dec-${i.id}`}>−</Button>}
                          <span className={low ? "text-destructive font-medium" : "font-medium"}>{i.quantity} {i.unit}</span>
                          {canManage && <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => adjust(i, 1)} data-testid={`inc-${i.id}`}>+</Button>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">{i.min_quantity}</TableCell>
                      <TableCell>{i.supplier || "—"}</TableCell>
                      <TableCell>{i.expiry_date || "—"}</TableCell>
                      {canManage && <TableCell><div className="inline-flex"><Button size="icon" variant="ghost" onClick={() => openManualEdit(i)} data-testid={`edit-inventory-${i.id}`}><Pencil className="w-4 h-4 text-primary" /></Button><Button size="icon" variant="ghost" onClick={() => removeStock(i.id)} data-testid={`delete-inventory-${i.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button></div></TableCell>}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* MERCI RICEVUTE */}
        <TabsContent value="merci">
          <div className="flex flex-wrap justify-end gap-2 mb-4">
            {canManage && <Button onClick={openInspection} variant="outline" className="rounded-full" data-testid="inspection-qr-button"><ShieldCheck className="w-4 h-4 mr-1" /> QR Ispezione</Button>}
            <Button onClick={() => { setPFrom(""); setPTo(""); setPrintOpen(true); }} className="rounded-full" data-testid="print-batches-button"><Printer className="w-4 h-4 mr-1" /> Stampa PDF</Button>
          </div>
          <Card className="p-4 rounded-xl mb-6 flex flex-wrap items-end gap-4" data-testid="batches-filters">
            <div className="flex-1 min-w-[220px] space-y-1">
              <Label className="text-xs">Cerca (prodotto, lotto, fornitore, barcode, n. documento)</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="es. Mozzarella, L2026A, Vegi, 4521…" className="pl-9" data-testid="batches-search" />
              </div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Categoria</Label><Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="es. Formaggi" data-testid="batches-category" /></div>
            <div className="space-y-1"><Label className="text-xs">Mese</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="batches-month" /></div>
            <label className="flex items-center gap-2 text-sm h-10"><Switch checked={onlyExp} onCheckedChange={setOnlyExp} data-testid="batches-expiring" /> Solo in scadenza (14 gg)</label>
          </Card>

          <Card className="rounded-xl overflow-hidden">
            <Table data-testid="batches-table">
              <TableHeader><TableRow><TableHead>Prodotto</TableHead><TableHead>Lotto</TableHead><TableHead>Ricevuto</TableHead><TableHead>Fornitore</TableHead><TableHead>Scadenza</TableHead><TableHead className="text-right">Qtà</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nessuna merce ricevuta trovata. Si popola dalle fatture (Ricezione da fattura) o dall'inserimento manuale.</TableCell></TableRow>}
                {items.map((b) => {
                  const dl = b.expiry ? daysLeft(b.expiry) : null;
                  const exp = dl !== null && dl <= 14;
                  return (
                    <TableRow key={b.id} data-testid={`batch-row-${b.id}`} className={`cursor-pointer ${exp ? "bg-[hsl(var(--warning))]/10" : ""}`} onClick={() => setLotId(b.id)}>
                      <TableCell className="font-medium">{b.product}{b.label_image_path && <ImageIcon className="w-3.5 h-3.5 inline ml-1 text-primary" />}{b.trace_status === "incompleta" && <span className="ml-1 text-[10px] text-amber-600">incompleta</span>}</TableCell>
                      <TableCell>{b.lotto || "—"}</TableCell>
                      <TableCell>{b.received_date}</TableCell>
                      <TableCell>{b.supplier || "—"}{b.doc_number ? <span className="block text-[11px] text-muted-foreground">doc. {b.doc_number}</span> : null}</TableCell>
                      <TableCell>
                        {b.expiry ? <span className={exp ? "text-[hsl(var(--warning))] font-medium inline-flex items-center gap-1" : ""}>{exp && <AlertTriangle className="w-3.5 h-3.5" />}{b.expiry}{dl !== null && dl < 0 ? " (scaduto)" : exp ? ` (${dl}gg)` : ""}</span> : "—"}
                      </TableCell>
                      <TableCell className="text-right">{b.quantity} {b.unit}{b.quantity_remaining != null && b.quantity_remaining !== b.quantity ? <span className="block text-[11px] text-muted-foreground">resid. {b.quantity_remaining}</span> : null}</TableCell>
                      {canManage && <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}><Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => printLabel(b)} data-testid={`print-label-${b.id}`}><Tag className="w-3.5 h-3.5 mr-1" />Etichetta</Button><Button size="icon" variant="ghost" onClick={() => openEditBatch(b)} data-testid={`edit-batch-${b.id}`}><Pencil className="w-4 h-4 text-primary" /></Button><Button size="icon" variant="ghost" onClick={() => remove(b.id)} data-testid={`delete-batch-${b.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button></TableCell>}
                      {!canManage && <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}><Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => printLabel(b)} data-testid={`print-label-${b.id}`}><Tag className="w-3.5 h-3.5 mr-1" />Etichetta</Button></TableCell>}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* SCHEDE PRODOTTO */}
        <TabsContent value="schede">
          <ProductSheets canManage={canManage} />
        </TabsContent>
      </Tabs>
      <LotDetailDialog lotId={lotId} onClose={() => setLotId(null)} />
      <Dialog open={!!prodLots} onOpenChange={(o) => !o && setProdLots(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-lots-dialog">
          <DialogHeader><DialogTitle>Lotti in magazzino · {prodLots?.name}</DialogTitle></DialogHeader>
          {!prodLots?.lots ? <p className="text-sm text-muted-foreground">Carico…</p> : prodLots.lots.length === 0 ? <p className="text-sm text-muted-foreground">Nessun lotto registrato per questo prodotto.</p> : (
            <div className="overflow-x-auto"><Table>
              <TableHeader><TableRow><TableHead>Lotto</TableHead><TableHead>Ingresso</TableHead><TableHead>Scadenza</TableHead><TableHead className="text-right">Iniziale</TableHead><TableHead className="text-right">Residua</TableHead><TableHead>Fornitore</TableHead><TableHead>Documento</TableHead></TableRow></TableHeader>
              <TableBody>{prodLots.lots.map((b) => (
                <TableRow key={b.id} className="cursor-pointer" onClick={() => { setProdLots(null); setLotId(b.id); }} data-testid={`product-lot-row-${b.id}`}>
                  <TableCell className="font-medium">{b.lotto || "—"}</TableCell><TableCell>{b.received_date}</TableCell><TableCell>{b.expiry || "—"}{b.date_type ? <span className="text-[10px] text-muted-foreground ml-1">{b.date_type}</span> : null}</TableCell>
                  <TableCell className="text-right">{b.quantity} {b.unit}</TableCell><TableCell className="text-right">{b.quantity_remaining ?? b.quantity} {b.unit}</TableCell><TableCell>{b.supplier || "—"}</TableCell><TableCell>{b.doc_number || "—"}</TableCell>
                </TableRow>))}</TableBody>
            </Table></div>
          )}
        </DialogContent>
      </Dialog>

      {/* Inserimento / modifica prodotto manuale */}
      <Dialog open={manualOpen} onOpenChange={(o) => { setManualOpen(o); if (!o) setStockEditId(null); }}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="inventory-dialog">
          <DialogHeader><DialogTitle>{stockEditId ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle></DialogHeader>
          <datalist id="supplier-list">{suppliers.map((s) => <option key={s.id} value={s.name} />)}</datalist>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Nome</Label><Input value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} data-testid="inv-name-input" /></div>
              <div className="space-y-2"><Label>Categoria</Label><Input value={manual.category} onChange={(e) => setManual({ ...manual, category: e.target.value })} placeholder="es. Formaggi" data-testid="inv-category-input" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Quantità</Label><Input type="number" value={manual.quantity} onChange={(e) => setManual({ ...manual, quantity: e.target.value })} data-testid="inv-qty-input" /></div>
              <div className="space-y-2"><Label>Unità</Label><Input value={manual.unit} onChange={(e) => setManual({ ...manual, unit: e.target.value })} data-testid="inv-unit-input" /></div>
              <div className="space-y-2"><Label>Min.</Label><Input type="number" value={manual.min_quantity} onChange={(e) => setManual({ ...manual, min_quantity: e.target.value })} data-testid="inv-min-input" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Fornitore</Label><Input list="supplier-list" value={manual.supplier} onChange={(e) => setManual({ ...manual, supplier: e.target.value })} placeholder="Cerca o scrivi…" data-testid="inv-supplier-input" /></div>
              <div className="space-y-2"><Label>Scadenza</Label><Input type="date" value={manual.expiry_date} onChange={(e) => setManual({ ...manual, expiry_date: e.target.value })} data-testid="inv-expiry-input" /></div>
            </div>

            {!stockEditId && (
              <>
                <div className="space-y-2">
                  <Label>Lotto <span className="text-destructive">*</span></Label>
                  <Input value={manual.lotto} onChange={(e) => setManual({ ...manual, lotto: e.target.value })} placeholder="Numero di lotto (obbligatorio)" data-testid="inv-lotto-input" />
                </div>
                <div className="border-t border-border/60 pt-4 space-y-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Acquisto e contabilità</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Costo totale (€)</Label><Input type="number" step="0.01" value={manual.cost} onChange={(e) => setManual({ ...manual, cost: e.target.value })} placeholder="0 = nessuna spesa" data-testid="inv-cost-input" /></div>
                    <div className="space-y-2"><Label>Registro</Label>
                      <Select value={manual.ledger} onValueChange={(v) => setManual({ ...manual, ledger: v })}>
                        <SelectTrigger data-testid="inv-ledger-select"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="ufficiale">Ufficiale (con IVA)</SelectItem><SelectItem value="black">Black</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  {showIva && costPrev > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                      <div className="space-y-2"><Label>IVA (%)</Label><Input type="number" value={manual.tax_rate} onChange={(e) => setManual({ ...manual, tax_rate: e.target.value })} data-testid="inv-taxrate-input" /></div>
                      <p className="text-sm text-muted-foreground pb-2" data-testid="inv-iva-preview">Imponibile € {imponibilePrev.toFixed(2)} · IVA € {(costPrev - imponibilePrev).toFixed(2)}</p>
                    </div>
                  )}
                  <div className="space-y-2"><Label>Pagato con (esce da)</Label>
                    <Select value={manual.payment_method} onValueChange={(v) => setManual({ ...manual, payment_method: v })}>
                      <SelectTrigger data-testid="inv-method-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{WALLETS.map((w) => <SelectItem key={w.v} value={w.v}>{w.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter><Button onClick={saveManual} className="rounded-full" data-testid="save-inventory-button">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) setEditId(null); }}>
        <DialogContent data-testid="edit-batch-dialog">
          <DialogHeader><DialogTitle>Modifica lotto ricevuto</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="space-y-2"><Label>Prodotto</Label><Input value={editForm.product} onChange={(e) => setEditForm({ ...editForm, product: e.target.value })} data-testid="edit-batch-product" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Lotto</Label><Input value={editForm.lotto} onChange={(e) => setEditForm({ ...editForm, lotto: e.target.value })} data-testid="edit-batch-lotto" /></div>
              <div className="space-y-2"><Label>Fornitore</Label><Input value={editForm.supplier} onChange={(e) => setEditForm({ ...editForm, supplier: e.target.value })} data-testid="edit-batch-supplier" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Data ricezione</Label><Input type="date" value={editForm.received_date} onChange={(e) => setEditForm({ ...editForm, received_date: e.target.value })} data-testid="edit-batch-received" /></div>
              <div className="space-y-2"><Label>Scadenza</Label><Input type="date" value={editForm.expiry} onChange={(e) => setEditForm({ ...editForm, expiry: e.target.value })} data-testid="edit-batch-expiry" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Quantità</Label><Input type="number" value={editForm.quantity} onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })} data-testid="edit-batch-qty" /></div>
              <div className="space-y-2"><Label>Unità</Label><Input value={editForm.unit} onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })} data-testid="edit-batch-unit" /></div>
              <div className="space-y-2"><Label>Categoria</Label><Input value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} data-testid="edit-batch-category" /></div>
            </div>
            <div className="space-y-2"><Label>Note</Label><Input value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} data-testid="edit-batch-notes" /></div>
          </div>
          <DialogFooter><Button onClick={saveBatch} className="rounded-full" data-testid="save-batch-button">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={csvOpen} onOpenChange={(o) => { setCsvOpen(o); if (!o) { setCsvFile(null); setCsvPreview(null); } }}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="csv-import-dialog">
          <DialogHeader><DialogTitle>Importa magazzino da CSV</DialogTitle><DialogDescription className="text-xs">Anteprima delle righe: i prodotti con lo stesso nome vengono consolidati, ogni riga crea un lotto tracciato.</DialogDescription></DialogHeader>
          {csvBusy && !csvPreview ? (
            <div className="py-8 flex items-center justify-center text-sm text-muted-foreground gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Analisi del file…</div>
          ) : csvPreview ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Card className="p-3 rounded-xl text-center" data-testid="csv-rows"><p className="text-2xl font-semibold">{csvPreview.rows}</p><p className="text-xs text-muted-foreground">righe / lotti</p></Card>
                <Card className="p-3 rounded-xl text-center" data-testid="csv-products"><p className="text-2xl font-semibold">{csvPreview.products}</p><p className="text-xs text-muted-foreground">prodotti unici</p></Card>
                <Card className="p-3 rounded-xl text-center" data-testid="csv-suppliers"><p className="text-2xl font-semibold">{csvPreview.suppliers?.length || 0}</p><p className="text-xs text-muted-foreground">fornitori</p></Card>
              </div>
              <p className="text-xs text-muted-foreground">I prodotti con lo stesso nome verranno consolidati in un unico articolo di magazzino (quantità sommate). Ogni riga crea un lotto tracciato con scadenza e fornitore.</p>
              <div className="rounded-xl border border-border overflow-hidden">
                <Table>
                  <TableHeader><TableRow><TableHead>Prodotto</TableHead><TableHead>Lotto</TableHead><TableHead>Scadenza</TableHead><TableHead className="text-right">Qtà</TableHead><TableHead>Fornitore</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {(csvPreview.sample || []).map((r, i) => (
                      <TableRow key={i}><TableCell className="font-medium">{r.name}</TableCell><TableCell>{r.lotto || "—"}</TableCell><TableCell>{r.expiry_raw || "—"}</TableCell><TableCell className="text-right">{r.quantity} {r.unit}</TableCell><TableCell>{r.supplier || "—"}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {csvPreview.rows > (csvPreview.sample?.length || 0) && <p className="text-xs text-muted-foreground text-center">…e altre {csvPreview.rows - csvPreview.sample.length} righe</p>}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setCsvOpen(false)} data-testid="csv-cancel-button">Annulla</Button>
            <Button onClick={doCsvImport} disabled={csvBusy || !csvPreview} className="rounded-full" data-testid="csv-confirm-button">{csvBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileUp className="w-4 h-4 mr-1" /> Importa {csvPreview?.rows || ""} righe</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={printOpen} onOpenChange={setPrintOpen}>
        <DialogContent data-testid="print-period-dialog">
          <DialogHeader><DialogTitle>Stampa merci ricevute</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Scegli il periodo (lascia vuoto per stampare tutte le ricezioni). Vengono applicati anche i filtri di ricerca/categoria attivi.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Dal</Label><Input type="date" value={pFrom} onChange={(e) => setPFrom(e.target.value)} data-testid="print-from-input" /></div>
              <div className="space-y-2"><Label>Al</Label><Input type="date" value={pTo} onChange={(e) => setPTo(e.target.value)} data-testid="print-to-input" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={printPeriod} className="rounded-full" data-testid="confirm-print-button"><Printer className="w-4 h-4 mr-1" /> Genera PDF</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={inspOpen} onOpenChange={setInspOpen}>
        <DialogContent data-testid="inspection-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /> QR Controllo ispettivo</DialogTitle></DialogHeader>
          <div className="space-y-4 text-center">
            <p className="text-sm text-muted-foreground">Mostra questo QR all'ispettore: aprirà una pagina con <b>tutti i registri HACCP</b>, i <b>fornitori</b> e la <b>tracciabilità completa</b> (merci e schede prodotto). È protetto da <b>password</b> (visibile in Dashboard, cambia a ogni accesso).</p>
            <div className="flex justify-center p-4 bg-white rounded-xl" data-testid="insp-qr">
              {inspUrl && <QRCodeCanvas value={inspUrl} size={220} level="M" />}
            </div>
            <a href={inspUrl} target="_blank" rel="noreferrer" className="text-xs text-primary break-all underline" data-testid="insp-link">{inspUrl}</a>
          </div>
          <DialogFooter><Button onClick={downloadInspQr} className="rounded-full" data-testid="download-insp-qr"><Download className="w-4 h-4 mr-1" /> Scarica QR</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
